<template>
  <div class="rating-choices" :class="[size, { 'has-selection': selected !== null }]" data-testid="rating-choices">
    <button
      v-for="choice in RATING_CHOICES"
      :key="choice.value"
      type="button"
      class="choice"
      :class="{ selected: selected === choice.value }"
      :disabled="disabled"
      :data-testid="`rating-choice-${choice.value}`"
      @click="$emit('select', choice.value)"
    >
      <span class="emoji">{{ choice.emoji }}</span>
      <span class="label">{{ choice.label }}</span>
    </button>
  </div>
</template>

<script>
import { RATING_CHOICES } from './ratingChoiceOptions';

export default {
  name: 'RatingChoices',
  props: {
    selected: { type: Number, default: null },
    disabled: { type: Boolean, default: false },
    // 'full' = full-screen post-payment prompt (largest targets); 'inline' = the
    // pre-payment widget under the document items (still touch-sized, smaller).
    size: { type: String, default: 'full' }
  },
  data() {
    return { RATING_CHOICES };
  }
};
</script>

<style scoped>
.rating-choices {
  display: flex;
  gap: 2.88vw;
  justify-content: center;
}
.choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  background: #fff;
  border: 3px solid #e5e7eb;
  border-radius: 1.25rem;
  cursor: pointer;
  transition: transform 0.12s ease, border-color 0.12s ease, background 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}
.choice:disabled {
  cursor: default;
}
.choice.selected {
  border-color: #4c1d95;
  background: #f5f3ff;
  /* Overrides .choice's own 0.12s transform transition with a slower one, so
     this specific grow reads as a deliberate confirmation rather than the
     quick hover/press feedback the border-color/background still use. */
  transform: scale(1.3);
  transition: transform 0.6s ease, border-color 0.12s ease, background 0.12s ease;
}
/* Once any icon is selected, the other two fade out until gone - draws the
   eye to the one that was actually picked, right before this widget hands
   off to its own confirmed/submitting state (RatingScreen.vue /
   InlineRatingSection.vue). has-selection is only true while `selected` is
   non-null (see the root div's :class above). */
.rating-choices.has-selection .choice:not(.selected) {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.6s ease;
}

/* Full-screen (post-payment): large, well-spaced touch targets. */
.rating-choices.full .choice {
  min-width: 15vw;
  padding: 3vh 2vw;
}
.rating-choices.full .emoji {
  font-size: calc(5.5rem * var(--font-scale));
  line-height: 1;
}
.rating-choices.full .label {
  font-size: calc(1.6rem * var(--font-scale));
  font-weight: 600;
  color: #374151;
}

/* Inline (pre-payment, under the document items): smaller but still clearly
   tappable - never a tiny control. */
.rating-choices.inline {
  gap: 2.16vw;
  justify-content: center;
}
.rating-choices.inline .choice {
  min-width: 8rem;
  min-height: 6rem;
  padding: 0.9rem 1.2rem;
}
.rating-choices.inline .emoji {
  font-size: calc(3rem * var(--font-scale));
  line-height: 1;
}
.rating-choices.inline .label {
  font-size: calc(1.1rem * var(--font-scale));
  font-weight: 600;
  color: #374151;
}
</style>
